import appVariables from '@/config'
import { main } from '@caterdesk/ui--theme'
import { getCloudflareImageUrl } from '@fusion/media/cloudflare'
import { Avatar, Box, Card, CardMedia, Stack, Tooltip, Typography } from '@mui/material'
import NoImageAvailable from '@public/images/no-image-available.png'

import React, { PropsWithChildren } from 'react'
import { useInView } from 'react-intersection-observer'

type ItemCardMediaPropsV2 = {
  imageUrl?: string | null
  imageTitle?: string | null
  vendorName?: string | null
  vendorAvatar?: string | null
  quantityRemaining?: number | null
}

const ItemCardMedia: React.FC<ItemCardMediaPropsV2> = ({
  imageTitle,
  imageUrl,
  vendorName,
  vendorAvatar,
  quantityRemaining,
}) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
  })

  const url = imageUrl
    ? getCloudflareImageUrl({
        enabled: appVariables.CLOUDFLARE_OPTIMISE_IMAGE,
        src: imageUrl,
        width: 500,
        quality: 100,
      })
    : NoImageAvailable.src

  const avatarUrl = getCloudflareImageUrl({
    enabled: appVariables.CLOUDFLARE_OPTIMISE_IMAGE,
    src: vendorAvatar || '',
    width: 100,
    quality: 100,
  })

  const title = imageTitle || ''
  return (
    <CardMedia
      sx={{
        minHeight: 240,
        borderRadius: 1,
        alignContent: 'end',
      }}
      image={inView ? url : ''}
      title={title}
      ref={ref}>
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          left: 8,
        }}>
        {vendorAvatar && vendorName ? (
          <Stack padding={1} height="100%" direction="column-reverse">
            <Tooltip title={vendorName}>
              <Avatar sx={{ width: 44, height: 44 }} alt={vendorName} src={inView ? avatarUrl : ''}>
                &nbsp;
              </Avatar>
            </Tooltip>
          </Stack>
        ) : null}
      </Box>
      <Box>
        {typeof quantityRemaining === 'number' && quantityRemaining <= 10 ? (
          <Box
            sx={{
              padding: 1,
              display: 'flex',
              justifyContent: 'center',
              backgroundColor: 'rgba(0,0,0,0.6)',
              borderRadius: '0 0 4px 4px',
            }}>
            <Typography color={main.colors.text_contrast} variant="caption" fontWeight={500}>
              {quantityRemaining > 0 ? `Only ${quantityRemaining} Left` : 'Sold Out!'}
            </Typography>
          </Box>
        ) : null}
      </Box>
    </CardMedia>
  )
}

const CCItemCardV2: React.FC<
  PropsWithChildren<
    ItemCardMediaPropsV2 & {
      onClick?: () => void
    }
  >
> = ({
  children,
  imageTitle,
  imageUrl,
  quantityRemaining,
  vendorAvatar,
  vendorName,
  onClick = () => {},
}) => {
  return (
    <Card
      onClick={onClick}
      sx={{
        padding: 1.5,
        position: 'relative',
        height: '100%',
        boxShadow: 'none',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        minWidth: 270,
      }}>
      <Stack height="100%">
        <ItemCardMedia
          imageTitle={imageTitle}
          imageUrl={imageUrl}
          vendorAvatar={vendorAvatar}
          vendorName={vendorName}
          quantityRemaining={quantityRemaining}
        />
        {children}
      </Stack>
    </Card>
  )
}

export default React.memo(CCItemCardV2)
